// src/pages/admin/AdminUsersList.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminUsers, deleteAdminUser, suspendAdminUser } from '../../services/adminApi';
import ReusableAdminTable from './ReusableAdminTable';
import { useUserColumns } from './users/components/useUserColumns';
import Swal from 'sweetalert2';

const AdminUsersList = ({ roleCode, title }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const res = await getAdminUsers(roleCode);
            setUsers(res.data);
        } catch (error) {
            console.error(`Error fetching ${title}`, error);
            setUsers([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [roleCode]);

    const handleSuspend = async (id) => {
        const userToSuspend = users.find(user => user.id === id);
        if (!userToSuspend) return;

        const isSuspended = userToSuspend.is_suspended;

        const result = await Swal.fire({
            title: isSuspended ? 'Unsuspend User?' : 'Suspend User?',
            text: isSuspended
                ? 'User account will be activated again.'
                : 'User account will be suspended.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: isSuspended ? '#f59e0b' : '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: isSuspended ? 'Yes, Unsuspend' : 'Yes, Suspend',
            cancelButtonText: 'Cancel',
            background: '#071427',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                await suspendAdminUser(id);
                setUsers(prev =>
                    prev.map(user =>
                        user.id === id
                            ? { ...user, is_suspended: !user.is_suspended }
                            : user
                    )
                );

                Swal.fire({
                    title: 'Success!',
                    text: isSuspended
                        ? 'User unsuspended successfully.'
                        : 'User suspended successfully.',
                    icon: 'success',
                    timer: 1800,
                    showConfirmButton: false,
                    background: '#071427',
                    color: '#fff'
                });
            } catch (error) {
                console.error("Failed to suspend/unsuspend user:", error);
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not update user status.',
                    icon: 'error',
                    background: '#071427',
                    color: '#fff'
                });
            }
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete User?',
            text: "Are you sure you want to delete this user?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            background: '#071427',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                await deleteAdminUser(id);
                await Swal.fire({
                    title: 'Deleted!',
                    text: 'User deleted successfully.',
                    icon: 'success',
                    timer: 1800,
                    showConfirmButton: false,
                    background: '#071427',
                    color: '#fff'
                });
                fetchUsers();
            } catch (error) {
                console.error("Failed to delete user:", error);
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not delete the user.',
                    icon: 'error',
                    background: '#071427',
                    color: '#fff'
                });
            }
        }
    };

    const handleView = (id) => {
        if (String(roleCode) === "300") {
            navigate(`/admin/candidate-profile/${id}?mode=view`);
            return;
        }
        if (String(roleCode) === "200") {
            navigate(`/admin/director-profile/${id}?mode=view`);
            return;
        }
        if (String(roleCode) === "400") {
            navigate(`/admin/casting-profile/${id}?mode=view`);
            return;
        }
    };

    const handleAddNew = () => {
        if (String(roleCode) === "300") {
            navigate('/admin/candidate-profile/add');
            return;
        }
        if (String(roleCode) === "200") {
            navigate('/admin/director-profile/add');
            return;
        }
        if (String(roleCode) === "400") {
            navigate('/admin/casting-profile/add');
            return;
        }
    };

    const columns = useUserColumns({ users, handleView, handleDelete, handleSuspend });

    return (
        <ReusableAdminTable
            data={users}
            columns={columns}
            isLoading={isLoading}
            title={title}
            onAddNew={handleAddNew}
            addNewButtonText={`Add New ${title.replace(/s$/, '')}`}
            searchPlaceholder={`Search in ${title}...`}
            exportCsvFilename={`${title.toLowerCase().replace(/ /g, '_')}.csv`}
        />
    );
};

export default AdminUsersList;